/**
 * StableTrust SDK integration layer.
 *
 * This module is the single, contained boundary between the React app and the
 * `@fairblock/stabletrust` SDK. It centralises:
 *
 *   1. ethers v6 interop. The rest of the app runs on ethers v5; the SDK
 *      requires ethers v6 signers. We build v6 signers here (via the `ethers6`
 *      alias) from the same EIP-1193 provider the wallet already uses, so the
 *      v5/v6 split never leaks into UI code.
 *   2. Both SDK clients:
 *        - `ConfidentialTransferClient` — on-chain confidential transfers
 *          (wallet visible, user pays gas). Auto-apply pending is disabled so
 *          Secure Claim stays an explicit user action.
 *        - `AnonymousTransferClient` — relay-based anonymous transfers
 *          (sender hidden, relay pays gas). Optional; requires a Fairycloak
 *          relay URL/API key supplied via environment variables.
 *   3. Per-chain configuration. Clients are constructed with the app's own
 *      contract address for the active chain (NOT the SDK defaults) because the
 *      ElGamal key-derivation context is bound to the verifying contract
 *      address — using the wrong address would make existing balances
 *      undecryptable.
 *
 * The low-level confidential crypto (proof generation, decryption, ElGamal key
 * derivation) is sourced from the SDK in `src/crypto/confidentialCrypto.js`.
 */

import { ethers as ethers6 } from "ethers6";
import {
  ConfidentialTransferClient,
  AnonymousTransferClient,
  deriveKeys as sdkDeriveKeys,
  decryptCiphertext as sdkDecryptCiphertext,
  combineCiphertext as sdkCombineCiphertext,
  encodeTransferProof as sdkEncodeTransferProof,
  encodeWithdrawProof as sdkEncodeWithdrawProof,
  validateAnonymousAccountId,
  MAX_ANONYMOUS_ACCOUNT_ID_LENGTH,
  getStabletrustContractAddress,
  STABLETRUST_CONTRACTS_BY_CHAIN_ID,
} from "@fairblock/stabletrust";
import { generate_deterministic_keypair } from "../crypto/confidentialCrypto.js";
import { retargetHybridChain } from "../utils/hybridProvider.js";

// Re-export SDK primitives so callers have a single import surface.
export {
  ConfidentialTransferClient,
  AnonymousTransferClient,
  sdkDecryptCiphertext as decryptCiphertext,
  sdkCombineCiphertext as combineCiphertext,
  sdkEncodeTransferProof as encodeTransferProof,
  sdkEncodeWithdrawProof as encodeWithdrawProof,
  validateAnonymousAccountId,
  MAX_ANONYMOUS_ACCOUNT_ID_LENGTH,
  getStabletrustContractAddress,
  STABLETRUST_CONTRACTS_BY_CHAIN_ID,
};

// ───────────────────────── ethers v6 interop ─────────────────────────

/**
 * Wrap a raw EIP-1193 provider (MetaMask, WalletConnect, Privy, …) in an
 * ethers v6 BrowserProvider and return a v6 signer suitable for the SDK.
 *
 * @param {object} eip1193Provider - An EIP-1193 provider (`.request(...)`).
 * @param {string} [account] - Optional account to bind the signer to.
 * @returns {Promise<import("ethers6").Signer>}
 */
export async function getV6Signer(eip1193Provider, account) {
  if (!eip1193Provider || typeof eip1193Provider.request !== "function") {
    throw new Error("A valid EIP-1193 provider is required to build a signer");
  }
  // "any" network lets the signer follow wallet chain switches without throwing.
  const browserProvider = new ethers6.BrowserProvider(eip1193Provider, "any");
  return account
    ? await browserProvider.getSigner(account)
    : await browserProvider.getSigner();
}

/**
 * The SDK constructs a JsonRpcProvider from the chain RPC URL. Direct browser
 * fetches to public RPCs often fail with "Failed to fetch" (CORS / blocked
 * HTTP). Rebind the client to the wallet's BrowserProvider so reads go through
 * the wallet the same way the rest of the app does.
 */
function attachWalletProvider(client, signer) {
  const browserProvider = signer?.provider;
  if (!client || !browserProvider) return;
  client.provider = browserProvider;
  if (client.contract) {
    client.contract = client.contract.connect(browserProvider);
  }
}

/**
 * Extract the underlying EIP-1193 provider from an ethers v5 Web3Provider, or
 * accept an already-raw EIP-1193 provider.
 *
 * @param {object} providerLike - ethers v5 provider or raw EIP-1193 provider.
 * @returns {object|null}
 */
export function toEip1193(providerLike) {
  if (!providerLike) return null;
  // ethers v5 Web3Provider keeps the raw EIP-1193 provider on `.provider`.
  if (providerLike.provider && typeof providerLike.provider.request === "function") {
    return providerLike.provider;
  }
  if (typeof providerLike.request === "function") {
    return providerLike;
  }
  return null;
}

// ───────────────────── Shared SDK client options ─────────────────────

/**
 * Application name stored on backend request rows. The SDK defaults to "SDK"
 * if this is omitted — always pass this app's name explicitly.
 */
export const STABLETRUST_APPLICATION_NAME = "Stabletrust";

/**
 * Options for StableTrust IPFS + backend persistence (SDK 2.0.11+).
 * CRA requires the REACT_APP_ prefix for browser-exposed env vars.
 */
export function getStabletrustSdkOptions() {
  const ipfsUploadUrl =
    process.env.REACT_APP_STABLETRUST_IPFS_UPLOAD_URL ||
    process.env.STABLETRUST_IPFS_UPLOAD_URL ||
    undefined;
  const ipfsApiKey =
    process.env.REACT_APP_STABLETRUST_IPFS_API_KEY ||
    process.env.STABLETRUST_IPFS_API_KEY ||
    undefined;
  const ipfsGatewayUrl =
    process.env.REACT_APP_STABLETRUST_IPFS_GATEWAY_URL ||
    process.env.STABLETRUST_IPFS_GATEWAY_URL ||
    undefined;
  const apiBaseUrl =
    process.env.REACT_APP_BACKEND_API_URL ||
    process.env.STABLETRUST_API_BASE_URL ||
    undefined;

  return {
    applicationName: STABLETRUST_APPLICATION_NAME,
    ...(ipfsUploadUrl ? { ipfsUploadUrl } : {}),
    ...(ipfsApiKey ? { ipfsApiKey } : {}),
    ...(ipfsGatewayUrl ? { ipfsGatewayUrl } : {}),
    ...(apiBaseUrl ? { apiBaseUrl } : {}),
  };
}

/**
 * Reuse the app's already-derived ElGamal keypair inside the SDK client.
 * The published SDK keeps keys in `_keyCache` and has no public seeder, so
 * we populate that cache directly instead of prompting for a second EIP-712
 * signature on every confidential op.
 */
function seedClientKeyCache(client, account, keypair) {
  if (!client?._keyCache || !account || !keypair?.public_key || !keypair?.private_key) {
    return;
  }
  const keys = {
    publicKey: keypair.public_key,
    privateKey: keypair.private_key,
  };
  try {
    client._keyCache.set(ethers6.getAddress(account), keys);
  } catch {
    // Invalid address — still store the raw form below.
  }
  client._keyCache.set(account, keys);
  client._keyCache.set(String(account).toLowerCase(), keys);
}

// ───────────────────── ConfidentialTransferClient ─────────────────────

/**
 * Keep fee-aware ConfidentialTransferClient behavior but skip the SDK's
 * implicit applyPending before deposit/transfer/withdraw.
 */
function disableAutoApplyPending(client) {
  client._applyPendingIfNeeded = async () => false;
  return client;
}

// Cache clients by chainId|contractAddress|rpcUrl. Clients are read-only over
// JSON-RPC and stateless across signers (the signer is passed per call), so a
// shared instance per chain is safe and avoids re-instantiating WASM/contracts.
const _confidentialClients = new Map();

/**
 * Get (or lazily create) a ConfidentialTransferClient for a chain.
 *
 * @param {object} cfg
 * @param {string} cfg.rpcUrl - JSON-RPC URL for the chain.
 * @param {string} cfg.contractAddress - The app's StableTrust contract for this
 *   chain. Required — do not rely on SDK defaults, since key derivation is bound
 *   to this address.
 * @param {number} cfg.chainId - Numeric chain id.
 * @returns {ConfidentialTransferClient}
 */
export function getConfidentialClient({ rpcUrl, contractAddress, chainId }) {
  if (!rpcUrl) throw new Error("rpcUrl is required for the confidential client");
  if (!contractAddress) {
    throw new Error("contractAddress is required for the confidential client");
  }
  if (!chainId) throw new Error("chainId is required for the confidential client");

  const sdkOptions = getStabletrustSdkOptions();
  const key = `${chainId}|${String(contractAddress).toLowerCase()}|${rpcUrl}|${sdkOptions.ipfsUploadUrl || ""}|${sdkOptions.apiBaseUrl || ""}|${sdkOptions.applicationName || ""}|no-auto-apply`;
  let client = _confidentialClients.get(key);
  if (!client) {
    client = disableAutoApplyPending(
      new ConfidentialTransferClient(
        rpcUrl,
        contractAddress,
        Number(chainId),
        sdkOptions,
      ),
    );
    _confidentialClients.set(key, client);
  }
  return client;
}

/**
 * Build a ConfidentialTransferClient from a CHAINS config entry.
 * @param {object} chainConfig - An entry from `src/config/chains.js` CHAINS.
 */
export function getConfidentialClientForChain(chainConfig) {
  if (!chainConfig) throw new Error("chainConfig is required");
  const contractAddress = chainConfig.contractAddress;
  if (!contractAddress) {
    throw new Error(
      `Contract address is not configured for chain ${chainConfig.id || chainConfig.chainIdDecimal}. Set REACT_APP_<CHAIN>_CONTRACT_ADDRESS.`,
    );
  }
  return getConfidentialClient({
    rpcUrl: chainConfig.rpcUrl,
    contractAddress,
    chainId: chainConfig.chainIdDecimal,
  });
}

/**
 * Ready-to-use confidential client + ethers v6 signer for the active wallet.
 * Seeds the SDK key cache from the app's already-derived ElGamal keypair so
 * ops do not re-prompt for EIP-712 signing.
 *
 * @param {object} chainConfig - CHAINS entry (env-backed contractAddress)
 * @param {object} providerLike - ethers v5 Web3Provider or EIP-1193 provider
 * @param {string} account - Wallet address
 * @param {{ public_key?: string, private_key?: string }|null} keypair - App keypair
 */
export async function getReadyConfidentialSession(
  chainConfig,
  providerLike,
  account,
  keypair,
) {
  const eip1193 = toEip1193(providerLike);
  if (!eip1193) {
    throw new Error("Wallet provider is required for confidential operations");
  }
  if (!chainConfig?.contractAddress) {
    throw new Error("Chain contractAddress is not configured");
  }
  const expectedChainId = Number(chainConfig.chainIdDecimal);
  if (!expectedChainId) {
    throw new Error("Chain chainIdDecimal is not configured");
  }

  // Hybrid retarget only reroutes *reads*. eth_sendTransaction / signing still
  // use the wallet's active chain — so refuse to proceed if the wallet is on
  // a different network than the UI selection (e.g. UI on Sepolia, Privy on
  // Arbitrum One).
  if (typeof eip1193.request === "function") {
    let walletChainId = null;
    try {
      const hexId = await eip1193.request({ method: "eth_chainId" });
      walletChainId = parseInt(hexId, 16);
    } catch (err) {
      throw new Error(
        `Could not read wallet network. Switch to ${chainConfig.name || expectedChainId} and try again.`,
      );
    }
    if (walletChainId !== expectedChainId) {
      throw new Error(
        `Wallet is on the wrong network (chain ${walletChainId}). Switch to ${chainConfig.name || expectedChainId} and try again.`,
      );
    }
  }

  // Hybrid reads follow the UI-selected chain even if something else briefly
  // races the wallet provider.
  retargetHybridChain(eip1193, expectedChainId);
  const client = getConfidentialClientForChain(chainConfig);
  const signer = await getV6Signer(eip1193, account);
  attachWalletProvider(client, signer);
  seedClientKeyCache(client, account, keypair);
  return { client, signer };
}

/**
 * Apply pending confidential balance via the SDK.
 * ConfidentialTransferClient exposes this as `_applyPending`; the controlled
 * client exposes a public `applyPending`. Use whichever the installed SDK has.
 */
export async function applyPendingWithClient(client, signer, options = {}) {
  if (typeof client?.applyPending === "function") {
    return client.applyPending(signer, options);
  }
  if (typeof client?._applyPending === "function") {
    return client._applyPending(signer, options);
  }
  throw new Error("Installed @fairblock/stabletrust client does not support applyPending");
}

/** Shared options so UI returns after inclusion (matches current frontend UX). */
export const SDK_UX_OPTIONS = { waitForFinalization: false };

// ─────────────────────── ElGamal key derivation ───────────────────────

/**
 * Derive the deterministic ElGamal keypair for a wallet using the SDK's
 * `deriveKeys`. The SDK uses the exact same EIP-712 domain
 * (`ConfidentialTokens` / `DeriveElGamalKey`), context hash, and HKDF domain
 * context as the app's legacy derivation, so keys are identical and existing
 * accounts/balances remain valid.
 *
 * @param {object} eip1193Provider - Raw EIP-1193 provider for signing.
 * @param {object} domainContext - { chainId, contractAddress }.
 * @param {string} [account] - Optional account to bind the signer to.
 * @returns {Promise<{ public_key: string, private_key: string }>} Snake-case to
 *   match the app's existing keypair shape.
 */
export async function deriveElGamalKeypair(eip1193Provider, { chainId, contractAddress }, account) {
  if (!chainId || !contractAddress) {
    throw new Error("chainId and contractAddress are required for key derivation");
  }
  const signer = await getV6Signer(eip1193Provider, account);
  const { publicKey, privateKey } = await sdkDeriveKeys(
    signer,
    { chainId: Number(chainId), contractAddress },
    generate_deterministic_keypair,
  );
  // Normalise to the snake_case shape the app uses everywhere.
  return { public_key: publicKey, private_key: privateKey };
}

// ───────────────────── AnonymousTransferClient ─────────────────────

/**
 * Read Fairycloak relay configuration from the environment.
 * Anonymous transfers are opt-in and require relay access from Fairblock.
 * @returns {{ fairycloakUrl: string|undefined, apiKey: string|undefined }}
 */
export function getAnonymousRelayConfig() {
  return {
    fairycloakUrl: process.env.REACT_APP_FAIRYCLOAK_URL,
    apiKey: process.env.REACT_APP_FAIRYCLOAK_API_KEY,
  };
}

/** Whether the anonymous (relay) transfer path is configured and available. */
export function isAnonymousTransferEnabled() {
  return Boolean(getAnonymousRelayConfig().fairycloakUrl);
}

const _anonymousClients = new Map();

/**
 * Get (or lazily create) an AnonymousTransferClient for a chain.
 *
 * @param {object} cfg
 * @param {number} cfg.chainId - Numeric chain id.
 * @param {string} cfg.rpcUrl - JSON-RPC URL for the chain.
 * @param {string} cfg.diamondAddress - Env-backed StableTrust contract for this chain.
 * @returns {AnonymousTransferClient}
 */
export function getAnonymousClient({ chainId, rpcUrl, diamondAddress }) {
  const { fairycloakUrl, apiKey } = getAnonymousRelayConfig();
  if (!fairycloakUrl) {
    throw new Error(
      "Anonymous transfers are not configured. Set REACT_APP_FAIRYCLOAK_URL " +
        "(and optionally REACT_APP_FAIRYCLOAK_API_KEY) to enable them. " +
        "Contact hello@fairblock.network for relay access.",
    );
  }
  if (!chainId) throw new Error("chainId is required for the anonymous client");
  if (!rpcUrl) throw new Error("rpcUrl is required for the anonymous client");

  if (!diamondAddress) {
    throw new Error(
      "diamondAddress is required for the anonymous client. Pass the env-backed contract address.",
    );
  }

  const sdkOptions = getStabletrustSdkOptions();
  const key = `${fairycloakUrl}|${chainId}|${rpcUrl}|${String(diamondAddress).toLowerCase()}|${sdkOptions.ipfsUploadUrl || ""}|${sdkOptions.applicationName || ""}`;
  let client = _anonymousClients.get(key);
  if (!client) {
    client = new AnonymousTransferClient({
      fairycloakUrl,
      chainId: Number(chainId),
      rpcUrl,
      diamondAddress,
      ...(apiKey ? { apiKey } : {}),
      ...sdkOptions,
    });
    _anonymousClients.set(key, client);
  }
  return client;
}

/**
 * Build an AnonymousTransferClient from a CHAINS config entry.
 * @param {object} chainConfig - An entry from `src/config/chains.js` CHAINS.
 */
export function getAnonymousClientForChain(chainConfig) {
  if (!chainConfig) throw new Error("chainConfig is required");
  if (!chainConfig.contractAddress) {
    throw new Error(
      `Contract address is not configured for chain ${chainConfig.id || chainConfig.chainIdDecimal}. Set REACT_APP_<CHAIN>_CONTRACT_ADDRESS.`,
    );
  }
  return getAnonymousClient({
    chainId: chainConfig.chainIdDecimal,
    rpcUrl: chainConfig.rpcUrl,
    diamondAddress: chainConfig.contractAddress,
  });
}
